"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";

export default function PortfolioGrid({ projects }) {
  const [filter, setFilter] = useState("all");

  const filtered = filter === "ai"
    ? projects.filter((p) => p.tags?.some((tag) => tag.includes("Artificial") || tag.includes("AI")))
    : projects;

  return (
    <div>
      {/* Filter buttons */}
      <div className="flex gap-2 mb-10">
        {[
          { key: "all", label: "All Projects" },
          { key: "ai", label: "AI Only" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              filter === key
                ? "bg-violet-600 text-white shadow-lg shadow-violet-500/25"
                : "bg-zinc-800/80 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 border border-white/5"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((project, i) => (
          <motion.div
            key={project.slug}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: i * 0.05 }}
          >
            <Link href={`/portfolio/${project.slug}`} className="group block h-full">
              <div className="h-full rounded-2xl bg-zinc-900 border border-white/[0.08] hover:border-violet-500/20 overflow-hidden transition-colors">
                <div className="aspect-[16/9] relative overflow-hidden bg-zinc-800">
                  <Image
                    src={project.image}
                    alt={project.title}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                    priority={i === 0}
                    fetchPriority={i === 0 ? "high" : "auto"}
                  />
                </div>
                <div className="p-6">
                  {project.tags?.[0] && (
                    <span className="inline-block text-xs font-semibold text-violet-400 uppercase tracking-widest mb-2">
                      {project.tags[0]}
                    </span>
                  )}
                  <h3 className="text-base font-semibold text-white mb-2 group-hover:text-violet-300 transition-colors line-clamp-2">
                    {project.title}
                  </h3>
                  <p className="text-sm text-zinc-500 leading-relaxed line-clamp-3">{project.description}</p>
                  {project.tech && project.tech.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-4">
                      {project.tech.slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 border border-white/[0.06] text-zinc-500">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-24 text-zinc-600">No projects found.</div>
      )}
    </div>
  );
}
